import React, { useState, useEffect, useCallback } from 'react';
import { Mail, RefreshCw, XCircle, Clock, CheckCircle, UserX, Copy, Check, Loader, Search, Plus, X, Send } from 'lucide-react';
import { getInvitations, createInvitation, resendInvitation, cancelInvitation } from '../../../api/invitations';
import { employeesAPI } from '../../../api/employees';

const STATUS_LABEL = {
  pending:  { label: 'Pendiente',  color: 'bg-amber-100 text-amber-700'  },
  accepted: { label: 'Aceptada',   color: 'bg-green-100 text-green-700'  },
  expired:  { label: 'Expirada',   color: 'bg-gray-100  text-gray-500'   },
  canceled: { label: 'Cancelada',  color: 'bg-red-100   text-red-600'    },
};

function CopyButton({ url }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  };
  return (
    <button
      onClick={copy}
      title="Copiar enlace de activación"
      className={`flex items-center gap-1 px-2 py-1 text-xs rounded-lg transition-colors ${
        copied ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
      }`}
    >
      {copied ? <Check size={12} /> : <Copy size={12} />}
      {copied ? 'Copiado' : 'Link'}
    </button>
  );
}

function InviteModal({ onClose, onCreated }) {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState('');
  const [selected, setSelected]   = useState(null);
  const [role, setRole]           = useState('employee');
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState('');
  const [activationUrl, setActivationUrl] = useState('');
  const [copied, setCopied]       = useState(false);

  useEffect(() => {
    employeesAPI.getAll({ limit: 500 })
      .then(r => {
        // Solo empleados sin usuario activo
        const all = r.employees || r.data?.employees || [];
        setEmployees(all.filter(e => !e.userId));
      })
      .catch(() => setEmployees([]))
      .finally(() => setLoading(false));
  }, []);

  const filtered = employees.filter(e =>
    !search ||
    `${e.nombre} ${e.apellido}`.toLowerCase().includes(search.toLowerCase()) ||
    e.email?.toLowerCase().includes(search.toLowerCase()) ||
    e.employeeId?.toLowerCase().includes(search.toLowerCase())
  );

  const handleSend = async () => {
    if (!selected) return;
    if (!selected.email) { setError('Este empleado no tiene email registrado'); return; }
    setSaving(true);
    setError('');
    try {
      const res = await createInvitation({ employeeId: selected.id, role });
      setActivationUrl(res.activationUrl);
      onCreated();
    } catch (e) {
      setError(e.response?.data?.error || 'Error al crear la invitación');
    } finally {
      setSaving(false);
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(activationUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-indigo-100 rounded-lg"><Mail size={18} className="text-indigo-600" /></div>
            <h2 className="text-base font-bold text-gray-800">Invitar empleado</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1"><X size={18} /></button>
        </div>

        <div className="p-6 space-y-4">

          {/* Si ya se envió → mostrar enlace */}
          {activationUrl ? (
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-xl">
                <CheckCircle size={20} className="text-green-600 shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-green-800">Invitación creada</p>
                  <p className="text-xs text-green-600">Comparte este enlace con <span className="font-semibold">{selected?.nombre} {selected?.apellido}</span></p>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Enlace de activación</label>
                <div className="flex gap-2">
                  <input readOnly value={activationUrl}
                    className="flex-1 text-xs border border-gray-200 rounded-lg px-3 py-2 bg-gray-50 font-mono text-gray-600 truncate" />
                  <button onClick={copyLink}
                    className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg transition-colors ${
                      copied ? 'bg-green-100 text-green-700' : 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200'
                    }`}>
                    {copied ? <Check size={13} /> : <Copy size={13} />}
                    {copied ? 'Copiado' : 'Copiar'}
                  </button>
                </div>
              </div>
              <button onClick={onClose}
                className="w-full py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200 transition-colors">
                Cerrar
              </button>
            </div>
          ) : (
            <>
              {/* Buscar empleado */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Empleado *</label>
                <div className="relative mb-2">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input value={search} onChange={e => setSearch(e.target.value)}
                    placeholder="Buscar por nombre, email o código..."
                    className="w-full pl-8 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300" />
                </div>
                <div className="border border-gray-200 rounded-lg overflow-hidden max-h-48 overflow-y-auto">
                  {loading ? (
                    <div className="text-center py-6 text-gray-400 text-sm"><Loader size={15} className="animate-spin mx-auto" /></div>
                  ) : filtered.length === 0 ? (
                    <div className="text-center py-6 text-gray-400 text-sm">
                      {search ? 'Sin resultados' : 'Todos los empleados ya tienen acceso'}
                    </div>
                  ) : (
                    filtered.map(emp => (
                      <button key={emp.id} onClick={() => setSelected(emp)}
                        className={`w-full flex items-center justify-between px-3 py-2.5 text-left text-sm transition-colors border-b last:border-0 border-gray-50 ${
                          selected?.id === emp.id ? 'bg-indigo-50 text-indigo-700' : 'hover:bg-gray-50 text-gray-700'
                        }`}>
                        <div>
                          <span className="font-medium">{emp.nombre} {emp.apellido}</span>
                          <span className="text-xs text-gray-400 ml-2 font-mono">{emp.employeeId}</span>
                        </div>
                        <span className={`text-xs truncate max-w-[140px] ${emp.email ? 'text-gray-400' : 'text-red-400 italic'}`}>
                          {emp.email || 'Sin email'}
                        </span>
                      </button>
                    ))
                  )}
                </div>
              </div>

              {/* Rol */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Rol asignado</label>
                <select value={role} onChange={e => setRole(e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300">
                  <option value="employee">Empleado</option>
                  <option value="rrhh">Recursos Humanos</option>
                  <option value="company_admin">Administrador</option>
                </select>
              </div>

              {error && (
                <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>
              )}

              <div className="flex gap-3 pt-1">
                <button onClick={onClose}
                  className="flex-1 py-2 border border-gray-200 text-gray-600 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors">
                  Cancelar
                </button>
                <button onClick={handleSend} disabled={!selected || saving}
                  className="flex-1 flex items-center justify-center gap-2 py-2 bg-indigo-600 text-white text-sm font-semibold rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors">
                  {saving ? <Loader size={14} className="animate-spin" /> : <Send size={14} />}
                  {saving ? 'Enviando...' : 'Enviar invitación'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function InvitationsTab() {
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [search, setSearch]           = useState('');
  const [filterStatus, setFilter]     = useState('all');
  const [busy, setBusy]               = useState(null);
  const [activationLinks, setLinks]   = useState({});
  const [showModal, setShowModal]     = useState(false);

  const frontendUrl = window.location.origin;

  const load = useCallback(() => {
    setLoading(true);
    getInvitations()
      .then(d => setInvitations(d.invitations || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleResend = async (inv) => {
    setBusy(inv.id);
    try {
      const res = await resendInvitation(inv.id);
      setLinks(prev => ({ ...prev, [inv.id]: res.activationUrl }));
      load();
    } catch (e) {
      alert(e.response?.data?.error || 'Error al reenviar');
    } finally {
      setBusy(null);
    }
  };

  const handleCancel = async (inv) => {
    if (!window.confirm(`¿Cancelar la invitación de ${inv.employee_nombre}?`)) return;
    setBusy(inv.id);
    try {
      await cancelInvitation(inv.id);
      load();
    } catch (e) {
      alert(e.response?.data?.error || 'Error al cancelar');
    } finally {
      setBusy(null);
    }
  };

  const filtered = invitations.filter(inv => {
    const matchSearch = !search ||
      inv.employee_nombre?.toLowerCase().includes(search.toLowerCase()) ||
      inv.email?.toLowerCase().includes(search.toLowerCase());
    const matchStatus = filterStatus === 'all' || inv.status === filterStatus;
    return matchSearch && matchStatus;
  });

  const counts = {
    pending:  invitations.filter(i => i.status === 'pending').length,
    accepted: invitations.filter(i => i.status === 'accepted').length,
    expired:  invitations.filter(i => i.status === 'expired').length,
    canceled: invitations.filter(i => i.status === 'canceled').length,
  };

  return (
    <div className="space-y-4">

      {showModal && (
        <InviteModal
          onClose={() => setShowModal(false)}
          onCreated={() => { load(); }}
        />
      )}

      {/* Stats */}
      <div className="grid grid-cols-4 gap-3">
        {[
          { key: 'pending',  label: 'Pendientes', icon: Clock,        color: 'text-amber-600' },
          { key: 'accepted', label: 'Aceptadas',  icon: CheckCircle,  color: 'text-green-600' },
          { key: 'expired',  label: 'Expiradas',  icon: UserX,        color: 'text-gray-500'  },
          { key: 'canceled', label: 'Canceladas', icon: XCircle,      color: 'text-red-500'   },
        ].map(s => {
          const Icon = s.icon;
          return (
            <button
              key={s.key}
              onClick={() => setFilter(f => f === s.key ? 'all' : s.key)}
              className={`text-center p-3 rounded-xl border transition-all ${
                filterStatus === s.key ? 'border-blue-300 bg-blue-50' : 'border-gray-200 bg-white hover:bg-gray-50'
              }`}
            >
              <div className={`text-2xl font-bold ${s.color}`}>{counts[s.key]}</div>
              <div className="text-xs text-gray-500 flex items-center justify-center gap-1 mt-0.5">
                <Icon size={11} /> {s.label}
              </div>
            </button>
          );
        })}
      </div>

      {/* Search + Invite button */}
      <div className="flex items-center gap-2">
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-semibold rounded-lg hover:bg-indigo-700 transition-colors shrink-0"
        >
          <Plus size={15} /> Invitar empleado
        </button>
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Buscar por empleado o email..."
            className="w-full pl-8 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
          />
        </div>
        <button
          onClick={load}
          className="p-2 border border-gray-200 rounded-lg text-gray-500 hover:bg-gray-50 transition-colors"
          title="Recargar"
        >
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      {/* Table */}
      <div className="border border-gray-200 rounded-xl overflow-hidden bg-white">
        {loading ? (
          <div className="text-center py-12 text-gray-400 flex items-center justify-center gap-2">
            <Loader size={16} className="animate-spin" /> Cargando invitaciones...
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12">
            <Mail className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 text-sm">No hay invitaciones{search ? ' que coincidan' : ''}</p>
            <p className="text-gray-400 text-xs mt-1">
              Invita a empleados desde su ficha en el módulo de Empleados
            </p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Empleado</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Email</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Rol</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Estado</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Expira</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filtered.map(inv => {
                const st  = STATUS_LABEL[inv.status] || STATUS_LABEL.expired;
                const isBusy = busy === inv.id;
                const link = activationLinks[inv.id] || (inv.token ? `${frontendUrl}/activar-cuenta?token=${inv.token}` : null);

                return (
                  <tr key={inv.id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-800">{inv.employee_nombre}</div>
                      <div className="text-xs text-gray-400 font-mono">{inv.employee_code}</div>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{inv.email}</td>
                    <td className="px-4 py-3">
                      <span className="text-xs px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded-full font-medium capitalize">
                        {inv.role}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${st.color}`}>
                        {st.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500">
                      {inv.status === 'accepted' && inv.accepted_at
                        ? `Aceptada ${new Date(inv.accepted_at).toLocaleDateString('es-DO')}`
                        : inv.expires_at
                          ? new Date(inv.expires_at).toLocaleDateString('es-DO', { day: '2-digit', month: 'short', year: 'numeric' })
                          : '—'
                      }
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        {inv.status === 'pending' && (
                          <>
                            {link && <CopyButton url={link} />}
                            <button
                              onClick={() => handleResend(inv)}
                              disabled={isBusy}
                              title="Regenerar enlace"
                              className="flex items-center gap-1 px-2 py-1 text-xs bg-amber-100 text-amber-700 rounded-lg hover:bg-amber-200 disabled:opacity-50 transition-colors"
                            >
                              {isBusy ? <Loader size={12} className="animate-spin" /> : <RefreshCw size={12} />}
                              Reenviar
                            </button>
                            <button
                              onClick={() => handleCancel(inv)}
                              disabled={isBusy}
                              title="Cancelar invitación"
                              className="p-1.5 text-red-400 hover:bg-red-50 rounded-lg disabled:opacity-50 transition-colors"
                            >
                              <XCircle size={15} />
                            </button>
                          </>
                        )}
                        {(inv.status === 'expired' || inv.status === 'canceled') && activationLinks[inv.id] && (
                          <CopyButton url={activationLinks[inv.id]} />
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
